import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import PropTypes from 'prop-types'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Loader from 'examples/Loader'
import { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { useAppServices } from 'hook/services'
import AvatarPreview from './components/AvatarPreview'
import ButtonFormModal from './components/ButtonFormModal'
import ButtonsTable from './components/ButtonsTable'
import IdentityFields from './components/IdentityFields'
import { BLUE, blueSwitchSx, isRenderable, moveItem, NAME_MAX } from './utils'

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={700}
    color="text.secondary"
    sx={{ letterSpacing: 1, display: 'block', mb: 1 }}
  >
    {children}
  </Typography>
)

SectionLabel.propTypes = { children: PropTypes.node.isRequired }

/** Shows the admin roughly what a visitor will see — header, greeting, chips. */
const Preview = ({ form, showButtons }) => (
  <Box sx={{ borderRadius: 2, border: `1px solid ${BLUE.line}`, overflow: 'hidden' }}>
    <Box
      sx={{
        backgroundColor: '#1C1C2E',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      <AvatarPreview src={form.avatarUrl} size={42} radius={6} iconSize={24} />

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 15 }} noWrap>
            {form.botName || 'Unnamed'}
          </Typography>

          {form.showStatus && form.statusText ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                borderRadius: 5,
                backgroundColor: 'rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.8)',
                fontSize: 11,
                whiteSpace: 'nowrap',
              }}
            >
              <Box
                sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#4DE8A0' }}
              />
              {form.statusText}
            </Box>
          ) : null}
        </Box>

        {form.tagline ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }} noWrap>
            {form.tagline}
          </Typography>
        ) : null}
      </Box>
    </Box>

    <Box sx={{ height: 3, backgroundColor: '#F0AD5B' }} />

    <Box sx={{ p: 2, backgroundColor: '#FFF8EE' }}>
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', color: '#1A1A2E', mb: showButtons ? 1.5 : 0 }}
      >
        {form.blurb || 'Your greeting shows here.'}
      </Typography>

      {showButtons ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {form.buttons.filter(isRenderable).map((button, index) => (
            <Chip
              key={button._id || `preview-${index}`}
              label={button.label}
              size="small"
              sx={{
                backgroundColor: '#fff',
                color: '#BD7A28',
                border: '1.5px solid #F0AD5B',
                fontWeight: 600,
              }}
            />
          ))}
        </Box>
      ) : null}
    </Box>

    {form.teaser ? (
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: `1px solid ${BLUE.line}`,
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Launcher bubble
        </Typography>
        <Box
          sx={{
            backgroundColor: '#1C1C2E',
            color: '#fff',
            fontSize: 12,
            px: 1.5,
            py: 0.5,
            borderRadius: 1.5,
          }}
        >
          {form.teaser}
        </Box>
      </Box>
    ) : null}
  </Box>
)

Preview.propTypes = {
  form: PropTypes.object.isRequired,
  showButtons: PropTypes.bool.isRequired,
}

/** One API row, shaped for the form. */
const toState = (config) => ({
  botName: config?.botName || '',
  tagline: config?.tagline || '',
  statusText: config?.statusText || '',
  showStatus: config?.showStatus !== false,
  avatarUrl: config?.avatarUrl || '',
  teaser: config?.teaser || '',
  blurb: config?.blurb || '',
  placeholder: config?.placeholder || '',
  showButtons: config?.showButtons !== false,
  buttons: config?.buttons || [],
})

function ChatBotConfig() {
  const AppService = useAppServices()

  const [loader, setLoader] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(null)
  const [form, setForm] = useState({
    botName: '',
    tagline: '',
    statusText: '',
    showStatus: true,
    avatarUrl: '',
    teaser: '',
    blurb: '',
    placeholder: '',
    showButtons: true,
    buttons: [],
  })

  const load = async () => {
    setLoader(true)

    const { response, error: apiError } = await AppService.chatbotConfig.get()

    if (!response?.config) {
      setError(apiError?.message || 'Could not load the chat settings.')
      setLoader(false)
      return
    }

    const next = toState(response.config)

    setForm(next)
    setSaved(next)
    setError('')
    setLoader(false)
  }

  useEffect(() => {
    load()
  }, [])

  // Compared against the last saved copy so the Save button only lights up when
  // there is something to save.
  const dirty = useMemo(
    () => Boolean(saved) && JSON.stringify(form) !== JSON.stringify(saved),
    [form, saved]
  )

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }))
  const onText = (field) => (e) => setField(field)(e.target.value)

  const setButtons = (buttons) => setForm((prev) => ({ ...prev, buttons }))

  const handleAdd = (button) => setButtons([...form.buttons, button])

  const handleChange = (index, button) =>
    setButtons(form.buttons.map((row, i) => (i === index ? button : row)))

  const handleMove = (index, offset) => setButtons(moveItem(form.buttons, index, offset))

  const handleRemove = async (index) => {
    const button = form.buttons[index]

    const confirmed = await Swal.fire({
      title: 'Remove this button?',
      text: button?.label || '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      confirmButtonColor: '#d33',
    })

    if (!confirmed.isConfirmed) return

    setButtons(form.buttons.filter((row, i) => i !== index))
  }

  const handleSave = async () => {
    if (!String(form.botName).trim()) {
      setError('The assistant needs a name.')
      return
    }

    if (String(form.botName).trim().length > NAME_MAX) {
      setError(`The name is too long for the header (${NAME_MAX} characters max).`)
      return
    }

    if (!String(form.blurb).trim()) {
      setError('The greeting cannot be empty.')
      return
    }

    setSaving(true)
    setError('')

    const { response, error: apiError } = await AppService.chatbotConfig.update({
      payload: {
        botName: form.botName,
        tagline: form.tagline,
        // The switch decides whether the pill renders; the text is kept either
        // way, so turning it back on restores the wording instead of a blank.
        statusText: form.statusText,
        showStatus: form.showStatus,
        avatarUrl: form.avatarUrl,
        teaser: form.teaser,
        blurb: form.blurb,
        placeholder: form.placeholder,
        showButtons: form.showButtons,
        // Position comes from the order of this array, so the API can drop the
        // per-row order field entirely.
        buttons: form.buttons,
      },
    })

    setSaving(false)

    if (!response) {
      setError(apiError?.message || 'Could not save. Please try again.')
      return
    }

    // Take the saved row back from the API rather than trusting the local copy:
    // it carries the ids and ordering the server actually stored.
    const next = response.config ? toState(response.config) : form
    setForm(next)
    setSaved(next)

    Swal.fire({
      icon: 'success',
      title: 'Saved',
      text: 'Live now — visitors see it the next time they open the chat.',
      timer: 2200,
      showConfirmButton: false,
    })
  }

  const hidden = form.buttons.filter((button) => !isRenderable(button)).length

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Chat Assistant
                </MDTypography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {dirty ? (
                    <Chip
                      size="small"
                      label="Unsaved changes"
                      sx={{ backgroundColor: '#fff', color: BLUE.dark, fontWeight: 700 }}
                    />
                  ) : null}
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={!dirty || saving}
                    onClick={handleSave}
                    sx={{
                      backgroundColor: '#fff',
                      color: BLUE.main,
                      '&:hover': { backgroundColor: BLUE.soft },
                    }}
                  >
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                </Box>
              </MDBox>

              {loader ? (
                <MDBox pt={3} px={3} pb={3}>
                  <Loader />
                </MDBox>
              ) : (
                <MDBox pt={3} px={3} pb={3}>
                  {error ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  ) : null}

                  <SectionLabel>ASSISTANT IDENTITY</SectionLabel>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    The face and name at the top of the chat window, and the bubble beside the
                    closed chat button.
                  </Typography>

                  <IdentityFields form={form} onField={setField} onText={onText} />

                  <Divider sx={{ my: 4 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                      <SectionLabel>GREETING</SectionLabel>

                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="What the bot says first"
                        value={form.blurb}
                        onChange={onText('blurb')}
                        helperText="Explain what the assistant can be used for. Blank lines and **bold** are kept."
                      />

                      <TextField
                        fullWidth
                        sx={{ mt: 2 }}
                        label="Input box placeholder"
                        value={form.placeholder}
                        onChange={onText('placeholder')}
                        helperText="The grey hint inside the message box."
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <SectionLabel>PREVIEW</SectionLabel>
                      <Preview form={form} showButtons={form.showButtons} />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box>
                      <SectionLabel>SUGGESTION BUTTONS</SectionLabel>
                      <Typography variant="caption" color="text.secondary">
                        Shown under the greeting, in this order, until the visitor sends a
                        message.
                        {hidden
                          ? ` ${hidden} row${hidden > 1 ? 's' : ''} will not appear — switched off or incomplete.`
                          : ''}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.showButtons}
                            onChange={(e) => setField('showButtons')(e.target.checked)}
                            sx={blueSwitchSx}
                          />
                        }
                        label="Show buttons"
                      />
                      <Box
                        sx={{
                          '& .MuiButton-contained': {
                            backgroundColor: BLUE.main,
                            color: '#fff',
                            '&:hover': { backgroundColor: BLUE.dark },
                          },
                        }}
                      >
                        <ButtonFormModal onSubmit={handleAdd} />
                      </Box>
                    </Box>
                  </Box>

                  <ButtonsTable
                    buttons={form.buttons}
                    onChange={handleChange}
                    onMove={handleMove}
                    onRemove={handleRemove}
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  )
}

export default ChatBotConfig
